import { useHead, useSeoMeta } from '@unhead/react'
import { Link } from 'react-router-dom'
import {ArrowRight, Target, Users, Lightbulb} from 'lucide-react'

const values = [
  { icon: Target, title: 'Pedagogy first', body: 'Every tool we build is grounded in evidence-based math instruction. We side with educators when convenience and learning conflict.' },
  { icon: Users, title: 'Free and accessible', body: 'Calculator skills should not require a login or a paywall. Our calculator emulator and articles are open to everyone, on any device.' },
  { icon: Lightbulb, title: 'Honest computation', body: 'We teach when to use mental math and when a calculator earns its place — never the other way around.' },
]

export default function About() {
  useSeoMeta({
    title: 'About CalculatorSkills — Our Mission',
    description: 'Learn how CalculatorSkills.com helps students, educators, and professionals build calculator skills and basic math computation confidence.',
    ogImage: 'https://www.calculatorskills.com/og/about.jpg',
    twitterCard: 'summary_large_image',
  })
  useHead({
    link: [{ rel: 'canonical', href: 'https://www.calculatorskills.com/about' }],
  })

  return (
    <div className="bg-[#FAFAF7]">
      <section className="border-b-2 border-[#0A0A0A]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-16 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-[2px] bg-[#E63946]" />
                <p className="eyebrow">/ About</p>
              </div>
              <h1 className="headline text-4xl sm:text-5xl lg:text-7xl mb-6">
                A pedagogy-first<br/>
                home for<br/>
                <span className="text-[#E63946]">calculator skills.</span>
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-[#475569] leading-relaxed">
                CalculatorSkills.com exists because we believe foundational computation skills in math are still the bedrock of mathematical reasoning — not a relic. We build small, sharp, free tools that respect both the device and the learner.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-4">
            <p className="eyebrow text-[#E63946] mb-4">/ Values</p>
            <h2 className="headline text-3xl lg:text-5xl">What we stand for.</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <p className="text-[#475569] text-lg leading-relaxed">
              Three values guide every article and every line of calculator code we ship. They are simple on purpose.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[#0A0A0A]">
          {values.map((v, i) => (
            <div key={v.title} className={`p-8 lg:p-10 ${i < 2 ? 'md:border-r-2 border-[#0A0A0A]' : ''} ${i > 0 ? 'border-t-2 md:border-t-0 border-[#0A0A0A]' : ''}`}>
              <v.icon size={28} strokeWidth={1.5} className="mb-8" />
              <h3 className="headline text-2xl mb-3">{v.title}</h3>
              <p className="text-[#475569] text-[15px] leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="eyebrow text-[#E63946] mb-4">/ How we got here</p>
              <h2 className="headline text-3xl lg:text-5xl mb-6">A note from the team.</h2>
              <p className="text-[#FAFAF7]/70 text-[15px] leading-relaxed mb-4">
                We are educators, designers, and developers who watched too many students reach for a calculator before reading the question. The opposite extreme — banning calculators in exam prep — made even less sense. So we built this.
              </p>
              <p className="text-[#FAFAF7]/70 text-[15px] leading-relaxed">
                The Praxis Core Calculator emulator on this site is the same kind of tool we wished we'd had when preparing our own students. It is honest about what a basic math calculator online can and cannot do, and it never gets in the way of learning.
              </p>
            </div>
            <div className="lg:col-span-6">
              <img
                src="https://static.lumi.new/e0/e0985fa744434ae7b7ea8d574cdfc48e.png"
                alt="A teacher guiding an elementary student through basic math computation skills using a calculator"
                className="w-full border-2 border-[#FAFAF7]/20"
                loading="lazy"
              />
            </div>
          </div>

          <div className="mt-16 pt-10 border-t border-[#FAFAF7]/15 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-[#FAFAF7]/60 text-[15px]">Ready to try the calculator?</p>
            <Link to="/calculator" className="inline-flex items-center gap-3 bg-[#E63946] hover:bg-[#FAFAF7] hover:text-[#0A0A0A] text-white px-8 py-4 font-semibold tracking-tight transition-colors">
              Open the Calculator <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
